import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    FiHome,
    FiLock,
    FiEye,
    FiEyeOff,
    FiCheckCircle,
    FiAlertCircle
} from "react-icons/fi";
import API_BASE_URL from "../config";
import "../styles/UserVerification.css";

export default function ResetPassword() {
    const { state } = useLocation();
    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("error");
    const [logoUrl, setLogoUrl] = useState(null);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [isMatch, setIsMatch] = useState(false);
    const [hasUppercase, setHasUppercase] = useState(false);

    /* ==============================
       LOAD LOGO FROM SITE SETTINGS
    ============================== */
    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/site-settings`);
                const data = await res.json();

                if (data?.logo && data?.logoContentType) {
                    const base64 = `data:${data.logoContentType};base64,${data.logo}`;
                    setLogoUrl(base64);
                }
            } catch (err) {
                console.error("Failed to load logo:", err);
            }
        };

        fetchLogo();
    }, []);

    /* ==============================
   PASSWORD MATCH CHECK
============================== */
    useEffect(() => {
        if (!password || !confirmPassword) {
            setIsMatch(false);
        } else {
            setIsMatch(password === confirmPassword);
        }
    }, [password, confirmPassword]);


    /* ==============================
       PASSWORD STRENGTH CALCULATION
    ============================== */
    useEffect(() => {
        const calculateStrength = () => {
            let score = 0;

            const upper = /[A-Z]/.test(password);
            const number = /[0-9]/.test(password);
            const special = /[^A-Za-z0-9]/.test(password);

            // 🔥 Mandatory rule: no uppercase = always weak
            if (!upper) {
                setPasswordStrength(1);   // force weak
                setHasUppercase(false);
                return;
            }

            // Now calculate normally only if uppercase exists
            if (password.length >= 8) score += 1;
            if (upper) score += 1;
            if (number) score += 1;
            if (special) score += 1;

            setPasswordStrength(score);
            setHasUppercase(true);
        };

        calculateStrength();
    }, [password]);



    const getStrengthClass = () => {
        if (password.length === 0) return "";

        // ❌ No uppercase → always weak
        if (!hasUppercase) return "strength-weak";

        if (passwordStrength <= 2) return "strength-weak";
        if (passwordStrength === 3) return "strength-medium";
        return "strength-strong";
    };


    /* ==============================
       VALIDATION FUNCTIONS
    ============================== */
    const validateForm = () => {
        if (!password) {
            setMessage("Please enter your new password");
            setMessageType("error");
            return false;
        }

        if (password.length < 6) {
            setMessage("Password must be at least 6 characters long");
            setMessageType("error");
            return false;
        }

        if (!/[A-Z]/.test(password)) {
            setMessage("Password must contain at least one uppercase letter");
            setMessageType("error");
            return false;
        }


        if (!confirmPassword) {
            setMessage("Please confirm your password");
            setMessageType("error");
            return false;
        }

        if (password !== confirmPassword) {
            setMessage("Passwords do not match");
            setMessageType("error");
            return false;
        }

        return true;
    };

    /* ==============================
       RESET PASSWORD
    ============================== */
    const resetPassword = async () => {
        if (!validateForm()) return;

        if (!state?.email) {
            setMessage("Email not found. Please restart the password reset process.");
            setMessageType("error");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password/reset`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: state.email,
                    newPassword: password
                })
            });

            const data = await res.json();

            if (!res.ok) {
                setMessage(data.message || "Failed to reset password");
                setMessageType("error");
                toast.error(data.message);
            } else {
                setMessage("Password reset successful! Redirecting to login...");
                setMessageType("success");
                toast.success("Password reset successful 🎉");

                // Redirect to login after delay
                setTimeout(() => {
                    navigate("/login");
                }, 2000);
            }
        } catch (err) {
            console.error(err);
            setMessage("Something went wrong. Please try again.");
            setMessageType("error");
            toast.error("Failed to reset password");
        } finally {
            setLoading(false);
        }
    };

    /* ==============================
       HANDLE KEY PRESS
    ============================== */
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            resetPassword();
        }
    };

    return (
        <div className="premium-verification-page">

            {/* ==============================
         TOP RIGHT HOME BUTTON
      ============================== */}
            <div className="premium-verification-home-container">
                <Link to="/" className="premium-verification-home-btn">
                    <FiHome size={20} />
                    <span>Home</span>
                </Link>
            </div>

            {/* ==============================
         MAIN CONTENT
      ============================== */}
            <div className="premium-verification-container">
                <div className="premium-verification-form-container">
                    <div className="premium-verification-form-card">

                        {/* Logo */}
                        <div className="premium-verification-form-logo">
                            {logoUrl ? (
                                <img src={logoUrl} alt="Brand Logo" className="premium-verification-form-brand-logo" />
                            ) : (
                                <div className="premium-verification-form-brand-text">Justivon</div>
                            )}
                        </div>

                        <div className="premium-verification-form-header">
                            <h2 className="premium-verification-form-title">Reset Password</h2>
                            <p className="premium-verification-form-subtitle">
                                Create a new password for your account
                            </p>
                        </div>

                        {/* Email Info */}
                        {state?.email && (
                            <div className="email-info">
                                <p>Resetting password for:</p>
                                <div className="email-address">{state.email}</div>
                            </div>
                        )}

                        {/* Message Display */}
                        {message && (
                            <div className={`premium-verification-message ${messageType}`}>
                                {messageType === "error" ? <FiAlertCircle /> : <FiCheckCircle />}
                                <span>{message}</span>
                            </div>
                        )}

                        {/* New Password Field */}
                        <div className="premium-verification-form-group">
                            <label className="premium-verification-label">
                                <FiLock size={16} />
                                <span>New Password</span>
                            </label>
                            <div className="premium-verification-input-group premium-verification-password-group">
                                <input
                                    className="premium-verification-input"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter your new password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                />
                                <button
                                    className="premium-verification-password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    type="button"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                </button>
                            </div>

                            {/* Password Strength Indicator */}
                            {password && (
                                <div className={`password-strength ${getStrengthClass()}`}>
                                    <div className="strength-meter">
                                        <div className="strength-fill"></div>
                                    </div>
                                    <span>
                                        {passwordStrength <= 1 ? "Weak" :
                                            passwordStrength <= 2 ? "Medium" : "Strong"}
                                    </span>
                                </div>
                            )}

                            <div className="password-hint">
                                Must contain at least 8 characters, one uppercase letter, one number and one special character
                            </div>
                        </div>

                        {/* Confirm Password Field */}
                        <div className="premium-verification-form-group">
                            <label className="premium-verification-label">
                                <FiLock size={16} />
                                <span>Confirm Password</span>
                            </label>

                            <div className="premium-verification-input-group premium-verification-password-group">
                                <input
                                    className={`premium-verification-input ${confirmPassword && !isMatch ? "input-error" : ""
                                        }`}
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm your new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                />

                                <button
                                    className="premium-verification-password-toggle"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    type="button"
                                >
                                    {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                </button>
                            </div>

                            {/* Live Match Message */}
                            {confirmPassword && (
                                <div className={`password-match ${isMatch ? "match" : "not-match"}`}>
                                    {isMatch ? "✔ Passwords match" : "✖ Passwords do not match"}
                                </div>
                            )}

                            {/* Uppercase Warning */}
                            {password && !hasUppercase && (
                                <div className="password-match not-match">
                                    ✖ Must contain at least one uppercase letter
                                </div>
                            )}

                        </div>


                        {/* Reset Password Button */}
                        <button
                            className="premium-verification-submit-btn"
                            onClick={resetPassword}
                            disabled={
                                loading ||
                                !password ||
                                !confirmPassword ||
                                !isMatch ||
                                !hasUppercase
                            }
                        >
                            {loading ? (
                                <div className="premium-verification-spinner"></div>
                            ) : (
                                <FiCheckCircle size={18} />
                            )}
                            <span>
                                {loading ? "Resetting..." : "Reset Password"}
                            </span>
                        </button>


                        {/* Back to Login */}
                        <div className="back-to-login">
                            <span>Remembered your password?</span>
                            <Link to="/login" className="back-to-login-link">
                                Back to Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}